'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { constructQuery } from '@repo/shared/helpers';
import TasksTabById from 'src/parts/tabs/TasksTabById';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroupTasks } from 'src/api/admin/tasks';

export const Route = createFileRoute('/_admin/groups/$groupId/tasks/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
			filter: (search.filter as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const tasks = await getGroupTasks(params.groupId, { page: deps.page, sort: deps.sort, filter: deps.filter });
		return { tasks };
	},
	component: TasksPage,
});

function TasksPage() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { tasks: initialTasks } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: tasks, refetch: refetchTasks } = useQuery({
		queryKey: ['group-tasks', groupId, searchParams.page, searchParams.sort, searchParams.filter],
		queryFn: () =>
			getGroupTasks(groupId, { page: searchParams.page, sort: searchParams.sort, filter: searchParams.filter }),
		initialData: initialTasks,
	});

	const handleUpdate = () => {
		refetchTasks();
		queryClient.invalidateQueries({ queryKey: ['group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['group-tasks-overview', groupId] });
	};

	const handleReload = () => {
		refetchTasks();
	};

	return (
		<TasksTabById
			tasks={tasks}
			authenticatedUser={authenticatedUser!}
			onUpdate={handleUpdate}
			onReload={handleReload}
			onTaskSelected={(id) => navigate({ to: '/groups/$groupId/tasks/$taskId', params: { groupId, taskId: id } })}
		/>
	);
}
